import {takeUntil} from "rxjs"
import {assertNever} from "@cm/utils"
import {Vector2} from "@cm/math"
import {OperatorToolboxBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-toolbox-base"
import {ShiftableToolboxItem} from "@app/textures/texture-editor/operator-stack/operators/shared/toolbox/shiftable-toolbox-item"
import type {OperatorLayerAndMask} from "@app/textures/texture-editor/operator-stack/operators/layer-and-mask/operator-layer-and-mask"
import {LayerEditMode, LayerMoveMode} from "@app/textures/texture-editor/operator-stack/operators/layer-and-mask/operator-layer-and-mask-types"
import {SelectionMode} from "@common/helpers/canvas/canvas-base-toolbox-legacy/canvas-base-toolbox-item-base"
import {EventEmitter} from "@angular/core"
import {BrushTool} from "@app/textures/texture-editor/operator-stack/operators/shared/toolbox/brush-tool"
import {CanvasToolboxItem} from "@common/helpers/canvas/canvas-toolbox/canvas-toolbox-item"

export class LayerAndMaskToolbox extends OperatorToolboxBase<OperatorLayerAndMask> {
    readonly userInterationFinished = new EventEmitter<void>()

    constructor(operator: OperatorLayerAndMask) {
        super(operator, SelectionMode.None, true)

        // this.brushToolboxItem = new DrawableImageBrushToolboxItem(this, operator.callback.imageOpContextWebGL2)
        this.brushToolboxItem = new BrushTool(this)
        this.brushToolboxItem.brushStrokeUpdated.subscribe(() => {
            operator.requestEval()
            operator.markEdited()
        })
        this.brushToolboxItem.brushStrokeCompleted.subscribe(() => this.userInterationFinished.emit())

        this.shiftableMapToolboxItem = new ShiftableToolboxItem(this)
        this.shiftableMapToolboxItem.shiftInPixels = this.operator.mapOffsetInPixels
        this.shiftableMapToolboxItem.shiftInPixelsChanged.subscribe((shiftInPixels) => {
            const delta = shiftInPixels.sub(this.operator.mapOffsetInPixels)
            if (this.operator.$layerMoveMode() === LayerMoveMode.ImageAndMask) {
                // if we are moving both image and mask, we need to move the mask by the same amount
                this.shiftableMaskToolboxItem.shiftInPixels = Vector2.fromVector2Like(this.operator.maskOffsetInPixels).add(delta)
            }
            this.operator.mapOffsetInPixels = shiftInPixels
        })
        this.shiftableMapToolboxItem.endShifting.subscribe(() => this.userInterationFinished.emit())
        this.shiftableMapToolboxItem.showGuides = this.operator.$showGuides()
        this.operator.showGuidesChanged.pipe(takeUntil(this.unsubscribe)).subscribe((value) => (this.shiftableMapToolboxItem.showGuides = value))

        this.shiftableMaskToolboxItem = new ShiftableToolboxItem(this)
        this.shiftableMaskToolboxItem.shiftInPixels = this.operator.maskOffsetInPixels
        this.shiftableMaskToolboxItem.shiftInPixelsChanged.subscribe((shiftInPixels) => (this.operator.maskOffsetInPixels = shiftInPixels))
        this.shiftableMaskToolboxItem.endShifting.subscribe(() => this.userInterationFinished.emit())
        this.shiftableMaskToolboxItem.showGuides = false

        this.updateItemSelection()
        this.operator.layerEditModeChanged.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.updateItemSelection())
        this.operator.layerMoveModeChanged.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.updateItemSelection())
    }

    get isUserInteracting(): boolean {
        return this.brushToolboxItem.isDrawing || this.shiftableMapToolboxItem.isUserInteracting || this.shiftableMaskToolboxItem.isUserInteracting
    }

    override hitTest(point: Vector2): CanvasToolboxItem | null {
        this.brushToolboxItem.brushCursorOffset.set(-this.operator.maskOffsetInPixels.x, -this.operator.maskOffsetInPixels.y) // TODO this is a hack to sync offsets; clean this up
        return super.hitTest(point)
    }

    private updateItemSelection() {
        const layerEditMode = this.operator.$layerEditMode()
        switch (layerEditMode) {
            case LayerEditMode.Move:
                this.brushToolboxItem.selected = false
                this.shiftableMapToolboxItem.selected =
                    this.operator.$layerMoveMode() === LayerMoveMode.ImageAndMask || this.operator.$layerMoveMode() === LayerMoveMode.ImageOnly
                this.shiftableMaskToolboxItem.selected = this.operator.$layerMoveMode() === LayerMoveMode.MaskOnly
                break
            case LayerEditMode.Draw:
                this.brushToolboxItem.selected = true
                this.shiftableMapToolboxItem.selected = false
                this.shiftableMaskToolboxItem.selected = false
                break
            default:
                assertNever(layerEditMode)
        }
    }

    // readonly brushToolboxItem: DrawableImageBrushToolboxItem
    readonly brushToolboxItem: BrushTool
    private shiftableMapToolboxItem: ShiftableToolboxItem
    private shiftableMaskToolboxItem: ShiftableToolboxItem
}
